import React, { createRef, useEffect, useMemo, useState } from "react";
import QueryLoadQueries from "components/QueryBuilder/Elements/QueryLoadQueries";
import QueryExportData from "components/QueryBuilder/Elements/QueryExportData";
import { useMutation } from "@apollo/client";
import {
  CREATE_NAMED_QUERY,
  SAVE_QUERY,
  UPDATE_NAMED_QUERY,
} from "components/QueryBuilder/QueryBuilderQueries";
import { AppContext } from "contexts/AppContextProvider";
import { QueryBuilderContext } from "contexts/QueryBuilderContextProvider";
import { areArraysDifferent, getTextWidth } from "components/Utils/Utils";
import CheckBox from "rc-checkbox";
import {
  HiOutlineLockClosed,
  HiOutlinePencil,
  HiOutlineUserGroup,
} from "react-icons/hi";
import { findParentNodeByClass } from "helpers/DOM";
import clsx from "clsx";

const QuerySave = (props) => {
  const inputRef = createRef();
  const [savedQuery, setSavedQuery] = useState(null);
  const [displaySavedLabel, setDisplaySavedLabel] = useState(false);
  const [queryName, setQueryName] = useState("");
  const [shouldSaveQueryName, setShouldSaveQueryName] = useState(false);
  const [shouldSaveQuery, setShouldSaveQuery] = useState(false);
  const [isPrivate, setIsPrivate] = useState(true);
  const [queryLoaded, setQueryLoaded] = useState(false);
  const [mouseHoverPencil, setMouseOverPencil] = useState(false);
  const [displayMessage, setDisplayMessage] = useState(false);
  const [shouldResizeInput, setShouldResizeInput] = useState(false);
  const { currentProject } = React.useContext(AppContext).state;
  let { queryStructure, originalQueryStructure } =
    React.useContext(QueryBuilderContext).state;

  // parse it
  queryStructure = useMemo(
    () => (queryStructure ? JSON.parse(queryStructure) : []),
    [queryStructure]
  );

  /**
   * Compares the filters applied and all its values to check whether they have changed or not
   * @param {*} currentQuery
   * @param {*} queryStructure
   * @returns
   */
  const compareQueryFilters = (currentQuery, queryStructure) => {
    const compare = (data) =>
      data
        .filter(
          (parameter) =>
            parameter.kind === "filter_by" &&
            parameter.type === "dimensions_items"
        )
        .map((parameter) => ({
          ...parameter.logicalOperator,
          ...parameter.value,
        }));

    return (
      JSON.stringify(compare(currentQuery)) ===
      JSON.stringify(compare(queryStructure))
    );
  };

  /**
   * Once queryUrl is updated.. check if should display the controls to save the change
   */
  useEffect(() => {
    // Enable the controllers to save the query when the URL is changed by adding metrics/breaking outs or filters
    if (queryStructure) {
      // doest the query have the saved query?
      const query = queryStructure.find((p) => p.savedQuery);
      if (query && query.savedQuery.name !== queryName) {
        setQueryName(query.savedQuery.name);
        setIsPrivate(query.savedQuery.private);
        setSavedQuery({ ...query.savedQuery });
        setShouldSaveQueryName(false);
        return;
        // if the query was saved and changed...
      } else if (savedQuery && savedQuery.query) {
        // Check for old type of encoded queries.
        let currentQuery = JSON.parse(
          savedQuery.query.indexOf("%20") > -1
            ? decodeURIComponent(savedQuery.query)
            : savedQuery.query
        ).filter((key) => !key.savedQuery);

        const isQueryChanged =
          areArraysDifferent(currentQuery, queryStructure) ||
          compareQueryFilters(currentQuery, queryStructure);

        // if it does have the query, but the metrics, dimensions or filters  has changed...
        if (isQueryChanged) {
          setShouldSaveQuery(true);
          return;
        }
      } else if (
        queryStructure.length > 0 &&
        !savedQuery &&
        queryName.trim().length > 0
      ) {
        // the user may have changed the query (not the title) but it wasn't persisted yet
        setShouldSaveQuery(!queryLoaded);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryStructure]);

  /**
   * Manages to display the Saved Label when the query was saved
   */
  useEffect(() => {
    if (shouldSaveQueryName) {
      setDisplaySavedLabel(false);
    }
  }, [shouldSaveQueryName]);

  useEffect(() => {
    const onOutsideClick = (event) => {
      if (
        event?.target?.parentElement?.id === "bPencil" ||
        ~["bPencil", "btn-save-as-new"].indexOf(event?.target?.id) ||
        event?.target?.classList?.contains("query-option-button") ||
        event?.target?.classList?.contains("query-save-checkbox-input") ||
        event?.target?.classList?.contains("btn-apply") ||
        findParentNodeByClass(event?.target, "filter-menu") ||
        findParentNodeByClass(event?.target, "query-remove-container")
      ) {
        return;
      }
      if (event.target.id !== "queryName") {
        setShouldResizeInput(false);
        setShouldSaveQueryName(false);
        setShouldSaveQuery(false);
      }
    };
    window.addEventListener("click", onOutsideClick);
    return () => {
      window.removeEventListener("click", onOutsideClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Updates the controllers to enable the buttons to save the query
   * @param {*} e
   */
  const onEditChange = (e) => {
    // set the query name
    setQueryName(e.target.value);
  };

  /**
   * Triggered to save the current query as a new one OR save an existing one as a new one
   */
  const saveQuery = () => {
    // first, let's create the short url with the current change.
    createNameQuery({
      variables: {
        input: {
          tenantID: parseInt(localStorage.defaultTenantID),
          query: JSON.stringify(queryStructure),
          name: queryName,
          projectID: currentProject.projectObj.id,
          private: isPrivate,
        },
      },
    }).then(({ data }) => {
      onQuerySaved(data.createNamedQuery);
    });
  };

  /**
   * Saves a query or save an existing one as a new one
   * @param {*} isNewQuery
   */
  const onSaveQuery = (isNewQuery) => {
    if (queryName.trim().length > 0) {
      // only save the query if there are filters applied
      if (!queryStructure || queryStructure.length === 0) {
        // we don't set focus
        setDisplayMessage(true);
      } else {
        setShouldSaveQueryName(false);
        setShouldResizeInput(false);

        // if the query wasn't saved yet or it's being saved as a new query
        if (!savedQuery || isNewQuery) {
          saveQuery();
        } else {
          // if the query was already persisted... just update it
          updateNamedQuery();
        }
      }
    } else {
      inputRef.current.focus();
      setDisplayMessage(true);
    }
  };

  /**
   * Creates just a copy of the current query
   */
  const onSaveAsNew = () => {
    setSavedQuery(null);
    setQueryName(queryName + " copy");
    setShouldResizeInput(true);
    setShouldSaveQueryName(true);
  };

  /**
   * After saving the query (a new one or updating an existing one)... update some controls
   * @param {*} queryData
   */
  const onQuerySaved = (queryData) => {
    setSavedQuery({
      ...queryData,
      //TODO: Workaround because the backend is not returning the author
      author: {
        email: localStorage.email,
      },
    });
    setDisplaySavedLabel(true);
    setShouldSaveQuery(false);
    setShouldSaveQueryName(false);

    const query = JSON.parse(queryData.query).filter((p) => !p.savedQuery);
    delete queryData.__typename;
    query.push({
      savedQuery: { ...queryData },
    });

    saveShortUrl({
      variables: {
        tenantID: parseInt(localStorage.defaultTenantID),
        query: JSON.stringify(query),
        originalQuery: originalQueryStructure,
      },
    }).then(({ data: savedShortUrl }) => {
      window.history.replaceState(null, null, "#" + savedShortUrl.saveQuery);
    });
  };

  /**
   * Call the mutation to create a new query
   */
  const [createNameQuery, { loading: isSaving }] =
    useMutation(CREATE_NAMED_QUERY);

  /**
   * Creates a new short URL based in the current queryStructure
   */
  const [saveShortUrl] = useMutation(SAVE_QUERY);

  /**
   * Call the mutation to save an existing query
   */
  const [updateNamedQuery, { loading: isUpdating }] = useMutation(
    UPDATE_NAMED_QUERY,
    {
      variables: {
        id: savedQuery?.id,
        input: {
          tenantID: parseInt(localStorage.defaultTenantID),
          query: JSON.stringify(queryStructure),
          name: queryName,
          projectID: currentProject.projectObj.id,
          private: isPrivate,
        },
      },
      onCompleted: (data) => {
        onQuerySaved(data.updateNamedQuery);
      },
    }
  );

  /**
   * Triggers the save button when the key is pressed
   */
  const onKeyPress = (e) => {
    if (e.key === "Enter") onSaveQuery();
  };

  /**
   * Determines that the title is in edit mode and we should
   * enable the controls to persist the changes
   */
  const setEditMode = () => {
    inputRef.current.focus();
    if (!savedQuery || savedQuery?.author?.email === localStorage.email) {
      setShouldSaveQueryName(true);
      setShouldSaveQuery(false);
    } else {
      setShouldSaveQuery(true);
      setShouldSaveQueryName(false);
    }
    setShouldResizeInput(true);
  };

  /**
   * Allow the user to create a new query using the current query as reference to be saved differently
   */
  const createNewQuery = () => {
    setSavedQuery(null);
    setQueryName("");
  };

  const setNewQuery = () => {
    setQueryLoaded(true);
    setShouldSaveQuery(false);
    setShouldSaveQueryName(false);
    props.setNewQuery();
  };

  const queryNameLen = queryName.trim().length;

  // determines when the user could save
  const couldSave =
    (shouldSaveQuery && savedQuery && (!isSaving || !isUpdating)) ||
    (shouldSaveQuery && queryNameLen > 0);

  // determine how the user could save
  const couldSaveAs =
    (savedQuery && savedQuery.author?.email === localStorage.email) ||
    /* or in the case that the user filled the query name but clicked outside the controller ans has changed the query */
    (!savedQuery && shouldSaveQuery && queryNameLen > 0);

  return (
    <div className="pl-4 animated fadeIn mb-3 mt-3">
      <div className="row">
        <div className="col-12">
          <div className="row">
            <div className="col-5 query-input-column">
              <input
                ref={inputRef}
                id="queryName"
                className={clsx("query-name", {
                  "text-orange": mouseHoverPencil,
                })}
                placeholder="Unsaved Query"
                value={queryName}
                style={
                  queryNameLen > 0 && (shouldResizeInput || shouldSaveQueryName)
                    ? { width: "100%" }
                    : queryNameLen > 0 &&
                      !shouldResizeInput &&
                      !shouldSaveQueryName
                    ? {
                        width:
                          getTextWidth(queryName, "", "query-name") * 1.1 +
                          "px",
                      }
                    : null
                }
                onChange={onEditChange}
                onClick={setEditMode}
                onKeyPress={onKeyPress}
              />
              <div className="query-save-icons">
                {!shouldResizeInput && (
                  <>
                    <div className="icon-controller">
                      <HiOutlinePencil
                        onClick={setEditMode}
                        className="text-orange edit-icon-svg"
                        id="bPencil"
                        onMouseOver={() => setMouseOverPencil(true)}
                        onMouseOut={() => setMouseOverPencil(false)}
                      />
                      {savedQuery &&
                        (isPrivate ? (
                          <HiOutlineLockClosed className="text-gray" />
                        ) : (
                          <HiOutlineUserGroup className="text-gray" />
                        ))}
                    </div>
                    {savedQuery && (
                      <span className="label">
                        {isPrivate ? "Private" : "Shared"}
                      </span>
                    )}
                  </>
                )}{" "}
              </div>

              {(isSaving || isUpdating) && (
                <div className={"query-save-query-spinner"}>
                  <div className="circle-scale circle-scale--sm" />
                </div>
              )}

              {displaySavedLabel && (!isSaving || !isUpdating) && (
                <div className={"query-saved-label animated-label"}>
                  <span className="saved-label">Saved</span>
                </div>
              )}
            </div>
            <div className="col-4">
              {
                // show the save initial save
                shouldSaveQueryName && queryNameLen > 0 && (
                  <>
                    <div className={"query-save-buttons animated fadeIn"}>
                      <button
                        onClick={() => onSaveQuery()}
                        className="query-option-button  h-100 w-80 query-option-button--border query-option-button--strong-label"
                      >
                        Save
                      </button>
                      <span className="label mr-3">Unsaved</span>
                      <CheckBox
                        prefixCls="query-save-checkbox"
                        checked={!isPrivate}
                        onChange={() => setIsPrivate(!isPrivate)}
                      />
                      <span className="label">Share Query</span>
                    </div>
                  </>
                )
              }
              {couldSave &&
                !shouldSaveQueryName &&
                (!isSaving || !isUpdating) && (
                  <>
                    <div className={"query-save-buttons animated fadeIn"}>
                      {shouldSaveQuery && (
                        <span className="label">Query updated</span>
                      )}
                      {/* if the query belongs to the current logged user */}
                      {couldSaveAs && (
                        <button
                          onClick={() => onSaveQuery()}
                          className="query-option-button  h-100 w-80 query-option-button--border query-option-button--strong-label"
                        >
                          Save Changes
                        </button>
                      )}
                      {shouldSaveQuery && (
                        <button
                          id="btn-save-as-new"
                          onClick={() => onSaveAsNew()}
                          className="query-option-button  h-100 w-80 query-option-button--border query-option-button--strong-label"
                        >
                          Save as New
                        </button>
                      )}
                    </div>
                  </>
                )}
            </div>
            <div className="col-3 text-right">
              <div className="query-save-buttons">
                <QueryLoadQueries
                  savedQuery={savedQuery}
                  setNewQuery={setNewQuery}
                  onDeleteQuery={createNewQuery}
                />
                <QueryExportData dateRanges={props.dateRanges} />
              </div>
            </div>
          </div>
          {displayMessage && (
            <div className="row">
              <div className="col-12">
                <div className="invalid-feedback d-block">
                  {queryName.trim().length === 0
                    ? "Query Name is required"
                    : !queryStructure || queryStructure.length === 0
                    ? "Select at least one metric in the Query Builder"
                    : ""}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuerySave;
